import { useState, useEffect, useCallback } from 'react';
import { api } from '../../context/AuthContext';
import { KpiCard } from '../../components/dashboard/KpiCard';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Zap, DollarSign, Users, Activity, ChevronRight, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n, decimals = 4) =>
  typeof n === 'number' ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: decimals })}` : '$0.00';

const fmtNum = (n) =>
  typeof n === 'number' ? n.toLocaleString() : '0';

export default function AdminLlmUsagePage() {
  const [summary, setSummary] = useState(null);
  const [listData, setListData] = useState(null);
  const [drillUser, setDrillUser] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDrill, setLoadingDrill] = useState(false);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/llm-usage/summary');
      setSummary(data);
    } catch {
      toast.error('Failed to load usage summary');
    } finally {
      setLoadingSummary(false);
    }
  }, []);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/llm-usage');
      setListData(data);
    } catch {
      toast.error('Failed to load client usage list');
    } finally {
      setLoadingList(false);
    }
  }, []);

  const fetchDrill = useCallback(async (userId) => {
    setLoadingDrill(true);
    try {
      const { data } = await api.get(`/admin/llm-usage/${userId}`);
      setDrillData(data);
    } catch {
      toast.error('Failed to load client details');
    } finally {
      setLoadingDrill(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchList();
  }, [fetchSummary, fetchList]);

  const openDrill = (userId) => {
    setDrillUser(userId);
    fetchDrill(userId);
  };

  const closeDrill = () => {
    setDrillUser(null);
    setDrillData(null);
  };

  if (drillUser) {
    return (
      <DrillInView
        data={drillData}
        loading={loadingDrill}
        onBack={closeDrill}
      />
    );
  }

  const dailyData = summary?.top_clients?.length
    ? []
    : [];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">AI Usage & Cost</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {summary ? `${monthName(summary.month)} ${summary.year}` : 'Loading…'}
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Cost MTD"
          value={summary?.total_cost_mtd}
          format="currency"
          icon={DollarSign}
          iconColor="text-emerald-500"
          iconBg="bg-emerald-50 dark:bg-emerald-900/30"
          loading={loadingSummary}
        />
        <KpiCard
          label="Cost Today"
          value={summary?.cost_today}
          format="currency"
          icon={Zap}
          iconColor="text-indigo-500"
          iconBg="bg-indigo-50 dark:bg-indigo-900/30"
          loading={loadingSummary}
        />
        <KpiCard
          label="Active Clients MTD"
          value={summary?.active_users_mtd}
          icon={Users}
          iconColor="text-blue-500"
          iconBg="bg-blue-50 dark:bg-blue-900/30"
          loading={loadingSummary}
        />
        <KpiCard
          label="Calls Today"
          value={summary?.calls_today}
          icon={Activity}
          iconColor="text-violet-500"
          iconBg="bg-violet-50 dark:bg-violet-900/30"
          loading={loadingSummary}
        />
      </div>

      {/* Top Clients Table */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          <h2 className="font-semibold text-sm">All Clients — {listData ? monthName(listData.month) + ' ' + listData.year : ''}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th className="px-5 py-3 text-left font-medium">Client</th>
                <th className="px-4 py-3 text-left font-medium">Package</th>
                <th className="px-4 py-3 text-right font-medium">Cost</th>
                <th className="px-4 py-3 text-right font-medium">Calls</th>
                <th className="px-4 py-3 text-left font-medium">Top Model</th>
                <th className="px-4 py-3 text-right font-medium">Input Tokens</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {loadingList && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-slate-400 text-sm">Loading…</td>
                </tr>
              )}
              {!loadingList && (!listData?.rows?.length) && (
                <tr>
                  <td colSpan={7} className="px-5 py-8 text-center text-slate-400 text-sm">No LLM usage recorded yet.</td>
                </tr>
              )}
              {(listData?.rows || []).map((row) => (
                <tr
                  key={row.user_id}
                  className="border-b border-slate-50 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors"
                  onClick={() => openDrill(row.user_id)}
                >
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-800 dark:text-slate-100 truncate max-w-[160px]">
                      {row.company_name || row.email || row.user_id}
                    </div>
                    {row.company_name && (
                      <div className="text-xs text-slate-400 truncate max-w-[160px]">{row.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-0.5 rounded capitalize">
                      {row.package_type || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-600">
                    {fmt(row.cost_usd)}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-300">
                    {fmtNum(row.calls)}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {row.top_model ? row.top_model.replace(/_/g, '.') : '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-500 text-xs">
                    {fmtNum(row.input_tokens)}
                  </td>
                  <td className="px-3 py-3 text-slate-400">
                    <ChevronRight className="w-4 h-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function DrillInView({ data, loading, onBack }) {
  if (loading || !data) {
    return (
      <div className="p-6 space-y-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-slate-400 py-12 text-center">Loading…</div>
      </div>
    );
  }

  const { user, totals, by_model, by_feature, projects, daily_series } = data;
  const displayName = user?.company_name || user?.email || user?.user_id || '';

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1 text-sm text-indigo-600 hover:underline">
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div>
          <h1 className="text-xl font-bold font-heading">{displayName}</h1>
          <p className="text-xs text-slate-400">{user?.email} · {user?.package_type || 'no plan'} · {monthName(data.month)} {data.year}</p>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Total Cost" value={totals?.cost_usd} format="currency" icon={DollarSign} iconColor="text-emerald-500" iconBg="bg-emerald-50 dark:bg-emerald-900/30" />
        <KpiCard label="Total Calls" value={totals?.calls} icon={Activity} iconColor="text-indigo-500" iconBg="bg-indigo-50 dark:bg-indigo-900/30" />
        <KpiCard label="Input Tokens" value={totals?.input_tokens} icon={Zap} iconColor="text-blue-500" iconBg="bg-blue-50 dark:bg-blue-900/30" />
        <KpiCard label="Output Tokens" value={totals?.output_tokens} icon={Zap} iconColor="text-violet-500" iconBg="bg-violet-50 dark:bg-violet-900/30" />
      </div>

      {/* Daily Cost Chart */}
      {daily_series?.length > 0 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
          <h2 className="font-semibold text-sm mb-4">Daily Cost</h2>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={daily_series}>
              <defs>
                <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v.toFixed(3)}`} width={70} />
              <Tooltip formatter={(v) => [`$${Number(v).toFixed(4)}`, 'Cost']} />
              <Area type="monotone" dataKey="cost_usd" stroke="#10b981" fill="url(#costGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By Model */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-semibold text-sm">By Model</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th className="px-5 py-2 text-left font-medium">Model</th>
                <th className="px-4 py-2 text-right font-medium">Cost</th>
                <th className="px-4 py-2 text-right font-medium">Calls</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(by_model || {}).map(([model, d]) => (
                <tr key={model} className="border-b border-slate-50 dark:border-slate-800">
                  <td className="px-5 py-2 font-mono text-xs">{model.replace(/_/g, '.')}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-600">{fmt(d.cost_usd)}</td>
                  <td className="px-4 py-2 text-right text-slate-500">{fmtNum(d.calls)}</td>
                </tr>
              ))}
              {!Object.keys(by_model || {}).length && (
                <tr><td colSpan={3} className="px-5 py-4 text-slate-400 text-center text-sm">No data</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* By Feature */}
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-semibold text-sm">By Feature</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th className="px-5 py-2 text-left font-medium">Feature</th>
                <th className="px-4 py-2 text-right font-medium">Cost</th>
                <th className="px-4 py-2 text-right font-medium">Calls</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(by_feature || {}).map(([feat, d]) => (
                <tr key={feat} className="border-b border-slate-50 dark:border-slate-800">
                  <td className="px-5 py-2 text-xs text-slate-700 dark:text-slate-300">{feat.replace(/_/g, '.')}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-600">{fmt(d.cost_usd)}</td>
                  <td className="px-4 py-2 text-right text-slate-500">{fmtNum(d.calls)}</td>
                </tr>
              ))}
              {!Object.keys(by_feature || {}).length && (
                <tr><td colSpan={3} className="px-5 py-4 text-slate-400 text-center text-sm">No feature labels set</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-project breakdown */}
      {projects?.length > 1 && (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800">
          <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
            <h2 className="font-semibold text-sm">By Project</h2>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-slate-400 border-b border-slate-100 dark:border-slate-800">
                <th className="px-5 py-2 text-left font-medium">Project ID</th>
                <th className="px-4 py-2 text-right font-medium">Cost</th>
                <th className="px-4 py-2 text-right font-medium">Calls</th>
              </tr>
            </thead>
            <tbody>
              {(projects || []).map((p) => (
                <tr key={p.project_id} className="border-b border-slate-50 dark:border-slate-800">
                  <td className="px-5 py-2 font-mono text-xs">{p.project_id}</td>
                  <td className="px-4 py-2 text-right font-mono text-emerald-600">{fmt(p.cost_usd)}</td>
                  <td className="px-4 py-2 text-right text-slate-500">{fmtNum(p.calls)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function monthName(month) {
  return new Date(2000, (month || 1) - 1, 1).toLocaleString('default', { month: 'long' });
}
